import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import DialogCustom from 'commons/DialogCustom';
import GroupActionButton from 'commons/GroupActionButton';
import API from 'configs/API';
import { isEditionStatusAvailable } from 'configs/Constant';
import useDeleteArtwork from 'hooks/useDeleteArtwork';
import useRole from 'hooks/useRole';
import useSavedUser from 'hooks/useSavedUser';
import useUpdateArtwork from 'hooks/useUpdateArtwork';
import useUploadAudioS3 from 'hooks/useUploadAudioS3';
import useUploadImageS3 from 'hooks/useUploadImageS3';
import { ToastTopHelper } from 'utils/utils';

import EditArtworkCertificate from '../EditArtworkCertificate';
import EditArtworkPrice from '../EditArtworkPrice';
import useUpdateEditions from './hooks/useUpdateEditions';
import useStyles from './styles';

const EditArtworkActionBlock = ({ artworkFormState }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const user = useSelector((state) => state.auth.account.user);

  const { isCollectorOrGalleryOwner } = useRole();
  const queryClient = useQueryClient();

  const [isShowDialog, setIsShowDialog] = useState(false);
  const { mutate: deleteArtwork } = useDeleteArtwork();

  const { handleSubmit, watch, setValue, formState } = artworkFormState;
  const { defaultValues } = formState;
  const { checkedArtistUnknown } = watch();

  const { uploadListImage } = useUploadImageS3();
  const { uploadAudio } = useUploadAudioS3();
  const { id: artworkId } = useParams();
  const { mutate: updateArtwork } = useUpdateArtwork(artworkId);
  const { mutateAsync: updateEditions } = useUpdateEditions();
  const { mutate: savedUser } = useSavedUser();

  const navigate = useNavigate();

  const editions = useMemo(() => {
    return watch('editions');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watch('editions'))]);

  const isStatusAvailable = useMemo(() => {
    return isEditionStatusAvailable(editions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(editions)]);

  const handleUpdateArtwork = async (data) => {
    const listImages = data?.uploadFiles || [];
    const uploadS3Response = await uploadListImage(listImages);

    const allSuccessful = uploadS3Response.every((response) => response?.success === true);

    if (!allSuccessful) {
      ToastTopHelper.error(t('upload_file_failed'));
      return;
    }

    const statusUploadList = (data?.statusUploadFiles || []).filter(Boolean);
    if (statusUploadList.length > 0) {
      const statusUploadResp = await uploadListImage(statusUploadList);
      const statusAllOk = statusUploadResp.every((response) => response?.success === true);
      if (!statusAllOk) {
        ToastTopHelper.error(t('upload_file_failed'));
        return;
      }
    }

    const formData = {
      title: data?.title,
      images: data?.keyImages,
      year_created: data?.yearCreated,
      period_created: data?.periodYear,
      style: data?.style,
      medium: data?.medium,
      orientation: data?.orientation?.id,
      size: {
        height: data?.height,
        width: data?.width,
        depth: data?.depth,
      },
      description: data?.description,
      note: data?.note,
      price: data?.price,
      is_hide_price: data?.isHidePriceVisible,
      is_ask_price_visible: data?.isAskPriceVisible,
      contact_information: data?.contactInformation,
      is_public: data?.isPublic,
      subjects: data?.subjects,
      is_public_certificate: data?.isPublicCertificate,
      inventory_code: data?.inventoryCode,
      currency: data?.currency,
      show_comments: data?.show_collector_comment,
    };

    const originalComments = (defaultValues?.comments || []).filter(
      (c) => c && typeof c.id !== 'undefined',
    );
    const currentComments = (data?.comments || []).filter((c) => c && typeof c.id !== 'undefined');
    const originalMap = originalComments.reduce((acc, c) => {
      acc[c.id] = c;
      return acc;
    }, {});
    const ownerExistingComment = (defaultValues?.comments || []).find(
      (c) => c?.user?.id === (user?.id || user?.user?.id),
    );
    const visibilityUpdates = currentComments
      .filter((c) => {
        const prev = originalMap[c.id];
        return prev && prev.is_hidden !== c.is_hidden;
      })
      .map((c) => ({ id: c.id, is_hidden: Boolean(c.is_hidden) }));

    const collectorComment = data?.collector_comment;
    const showCollectorComment = Boolean(data?.show_collector_comment);

    if (
      visibilityUpdates.length > 0 ||
      collectorComment !== undefined ||
      data?.show_collector_comment !== undefined
    ) {
      formData.comment = {};
      if (visibilityUpdates.length > 0) formData.comment.visibility_updates = visibilityUpdates;
      if (collectorComment !== undefined) {
        formData.comment.content = collectorComment;
        if (ownerExistingComment?.id) formData.comment.id = ownerExistingComment.id;
      }
      if (data?.show_collector_comment !== undefined)
        formData.comment.show_collector_comment = showCollectorComment;
    }

    const existingKeyConds = (data?.keyConditionImages || []).filter(
      (v) => typeof v === 'string' && !v.startsWith('blob:'),
    );
    const uploadKeyConds = (data?.statusUploadFiles || [])
      .map((it) => it?.imageKey)
      .filter(Boolean);
    const mergedKeyConds = [...existingKeyConds];
    uploadKeyConds.forEach((k) => {
      if (!mergedKeyConds.includes(k)) mergedKeyConds.push(k);
    });

    if (mergedKeyConds.length > 0) {
      formData.key_condition_images = mergedKeyConds;
      formData.condition_images = mergedKeyConds;
    }

    // Handle audio upload per new flow: upload only on Update
    if (data?.audioRemoved) {
      formData.audio_file = null;
    } else if (data?.audioKey && data?.audioPendingFile instanceof File) {
      try {
        const pending = data.audioPendingFile;
        const key = data.audioKey;
        const uploadFile = new File([pending], key, { type: pending.type });
        const resp = await uploadAudio({
          audioKey: key,
          uploadFile,
          onUploadProgress: (evt) => {
            const total = evt?.total || 0;
            const loaded = evt?.loaded || 0;
            if (total > 0) {
              const pct = Math.round((loaded * 100) / total);
              setValue('audioUploadProgress', pct);
            }
          },
        });
        if (!resp?.success) {
          ToastTopHelper.error(t('upload_file_failed'));
          return;
        }
        formData.audio_file = key;
      } catch (e) {
        ToastTopHelper.error(t('upload_file_failed'));
        return;
      }
    } else if (data?.audioKey && !data?.audioPendingFile) {
      // Key already persisted previously
      formData.audio_file = data.audioKey;
    }

    if (isCollectorOrGalleryOwner) {
      const selectedUserId = data?.selectedUser?.id;
      formData.edition = data?.edition;

      if (selectedUserId) {
        formData.artist_id = selectedUserId;
        formData.artist_artwork = null;
      } else if (data?.artistName) {
        formData.artist_artwork = {
          artist_name: data?.artistName,
          year_of_birth: data?.yearOfBirthArtist,
        };
        formData.artist_id = null;
      }

      if (!checkedArtistUnknown) {
        savedUser({
          user: user?.id,
          connected_artist: selectedUserId,
        });
      }

      if (checkedArtistUnknown) {
        formData.artist_id = null;
        formData.artist_artwork = null;
      }
    }

    await updateEditions(data?.editions);

    updateArtwork(formData, {
      onSuccess: () => {
        ToastTopHelper.success(t('update_successfully'));
        navigate(-1);
      },
    });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleShowPopUpConfirmDelete = () => {
    setIsShowDialog(true);
  };

  const handleCancelDelete = () => {
    setIsShowDialog(false);
  };

  const handleDeleteArtwork = (e) => {
    e.stopPropagation();
    deleteArtwork(defaultValues?.artworkId, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [API.ARTWORK.LIST_GALLERY_ARTWORK] });
        ToastTopHelper.success(
          t(
            defaultValues?.hasCertificate
              ? 'text_move_artwork_to_store'
              : 'text_delete_artwork_success',
          ),
          navigate(-1),
        );
      },
    });

    setIsShowDialog(false);
  };

  useEffect(() => {
    if (!isStatusAvailable) {
      setValue('price', '');
      setValue('isAskPriceVisible', false);
      setValue('isHidePriceVisible', false);
      setValue('contactInformation', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStatusAvailable, JSON.stringify(formState?.defaultValues)]);

  useEffect(() => {
    if (isStatusAvailable) {
      setValue('price', defaultValues?.price);
      setValue('isAskPriceVisible', defaultValues?.isAskPriceVisible);
      setValue('isHidePriceVisible', defaultValues?.isHidePriceVisible);
      setValue('contactInformation', defaultValues?.contactInformation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStatusAvailable, JSON.stringify(formState?.defaultValues)]);

  return (
    <Box>
      {isStatusAvailable && <EditArtworkPrice artworkFormState={artworkFormState} />}

      <EditArtworkCertificate artworkFormState={artworkFormState} />

      {/* <EditArtworkVisible artworkFormState={artworkFormState} /> */}

      <GroupActionButton
        mainButtonText={t('update')}
        subButtonLeftText={t('cancel')}
        subButtonRightText={t('delete_artwork')}
        onMainButtonClick={handleSubmit(handleUpdateArtwork)}
        onSubButtonRight={handleShowPopUpConfirmDelete}
        onSubButtonLeft={handleCancel}
        groupActionButtonWrapStyle={classes.groupActionWrapper}
        subButtonRightStyle={classes.subButtonRightStyle}
      />

      <DialogCustom
        isShowDialog={isShowDialog}
        dialogTitle={t('delete_artwork')}
        textCancel={t('cancel')}
        textSubmit={t('delete')}
        onCancel={handleCancelDelete}
        onSubmit={handleDeleteArtwork}
        submitButtonStyle={clsx(classes.buttonDialog, classes.buttonDanger)}
        cancelButtonStyle={clsx(classes.buttonDialog)}
      >
        <div className={classes.wrapDialogContent}>
          {t(
            defaultValues?.hasCertificate
              ? 'text_confirm_delete_artwork_has_certificate'
              : 'text_confirm_delete_artwork_no_certificate',
          )}
        </div>
      </DialogCustom>
    </Box>
  );
};

export default EditArtworkActionBlock;
